import React, { useState } from 'react';
import { checkStatusWithOtp, checkExistingUser, ExistingUserCheck, fetchOtp } from '../../services/api/registration/RegisterServices';
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'react-toastify';
import './StatusCheck.css';

export default function StatusCheck() {
  const [mode, setMode] = useState<'choose' | 'mobile' | 'google'>('choose');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [verified, setVerified] = useState(false);
  const [verifiedLabel, setVerifiedLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ExistingUserCheck | null>(null);

  const sendOtp = async () => {
    if (mobile.length !== 10) { toast.warning('Enter a valid 10-digit mobile number'); return; }
    setLoading(true);
    try {
      await fetchOtp({ mobileNumber: mobile });
      setOtpSent(true);
      toast.success('OTP sent to +91 ' + mobile);
    } catch {
      toast.error('Failed to send OTP');
    } finally { setLoading(false); }
  };

  const verifyAndCheck = async () => {
    if (otp.length !== 6) { toast.warning('Enter a valid 6-digit OTP'); return; }
    setLoading(true);
    try {
      const checkRes = await checkStatusWithOtp(mobile, otp);
      setVerified(true);
      setVerifiedLabel('+91 ' + mobile);
      setResult(checkRes);
    } catch {
      toast.error('OTP verification failed');
    } finally { setLoading(false); }
  };

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    if (credentialResponse.credential) {
      try {
        const decoded: any = jwtDecode(credentialResponse.credential);
        const email = decoded.email || '';
        setLoading(true);
        const checkRes = await checkExistingUser(email);
        setVerified(true);
        setVerifiedLabel(email);
        setResult(checkRes);
      } catch {
        toast.error('Failed to verify Google account');
      } finally { setLoading(false); }
    }
  };

  const getStatusStep = (status: string) => {
    const s = status.toLowerCase();
    if (s === 'approved') return 3;
    if (s === 'rejected') return 3;
    if (s.includes('pending')) return 2;
    return 1;
  };

  const reset = () => {
    setMode('choose'); setMobile(''); setOtp(''); setOtpSent(false);
    setVerified(false); setVerifiedLabel(''); setResult(null);
  };

  return (
    <div className="status-page">
      <div className="status-card">
        <div className="status-header">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--blue-500)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <h2>Track Your Application</h2>
          <p>{verified ? 'Your application status' : 'Verify your identity to check status'}</p>
        </div>

        {/* ═══ CHOOSE METHOD ═══ */}
        {!verified && mode === 'choose' && (
          <div className="status-methods">
            <button className="status-method-btn google" onClick={() => setMode('google')}>
              <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Sign in with Google
            </button>
            <div className="status-divider"><span>or</span></div>
            <button className="status-method-btn mobile" onClick={() => setMode('mobile')}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
              Verify with Mobile OTP
            </button>
          </div>
        )}

        {/* ═══ GOOGLE SIGN-IN ═══ */}
        {!verified && mode === 'google' && (
          <div className="status-google-section">
            <p className="status-section-hint">Sign in with the Google account you used during registration</p>
            <div className="status-google-btn">
              <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => toast.error('Google Sign-In failed')} theme="outline" size="large" width="100%" />
            </div>
            <button className="status-back-link" onClick={() => setMode('choose')}>← Use mobile OTP instead</button>
          </div>
        )}

        {/* ═══ MOBILE OTP ═══ */}
        {!verified && mode === 'mobile' && (
          <>
            <div className="status-input-row">
              <div className="status-mobile-wrap">
                <span className="status-prefix">+91</span>
                <input
                  type="text"
                  className="status-input"
                  placeholder="Enter mobile number"
                  value={mobile}
                  onChange={e => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  disabled={otpSent}
                  maxLength={10}
                />
              </div>
              {!otpSent ? (
                <button className="status-btn" onClick={sendOtp} disabled={loading || mobile.length !== 10}>
                  {loading ? 'Sending...' : 'Send OTP'}
                </button>
              ) : (
                <button className="status-btn resend" onClick={sendOtp} disabled={loading}>Resend</button>
              )}
            </div>

            {otpSent && (
              <div className="status-otp-row">
                <input
                  type="text"
                  className="status-otp-input"
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                />
                <button className="status-verify-btn" onClick={verifyAndCheck} disabled={loading || otp.length !== 6}>
                  {loading ? 'Verifying...' : 'Verify & Check'}
                </button>
              </div>
            )}
            <button className="status-back-link" onClick={() => setMode('choose')}>← Use Google instead</button>
          </>
        )}

        {/* ═══ RESULTS ═══ */}
        {verified && result && (
          <div className="status-results">
            <div className="status-verified-strip">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              <span>Verified: {verifiedLabel}</span>
              <button className="status-reset-btn" onClick={reset}>Check another</button>
            </div>

            {!result.exists ? (
              <div className="status-empty">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--gray-300)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><line x1="8" y1="12" x2="16" y2="12"/>
                </svg>
                <h3>No applications found</h3>
                <p>No registration found with this {verifiedLabel.includes('@') ? 'email' : 'mobile number'}. <a href="/">Register now</a></p>
              </div>
            ) : (
              <>
                <div className="status-count-badge">
                  {result.count} application{result.count > 1 ? 's' : ''} found
                </div>
                {result.existingLocations.map((loc) => (
                  <div key={loc.id} className="status-location-card">
                    <div className="slc-header">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--blue-500)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                      </svg>
                      <span className="slc-name">{loc.name}</span>
                      <span className={`slc-badge ${loc.status.toLowerCase().replace(/\s+/g, '-')}`}>{loc.status}</span>
                    </div>
                    <div className="slc-timeline">
                      <div className={`tl-step ${getStatusStep(loc.status) >= 1 ? 'done' : ''}`}>
                        <div className="tl-dot" /><div className="tl-label"><strong>Submitted</strong><span>Application received</span></div>
                      </div>
                      <div className={`tl-step ${getStatusStep(loc.status) >= 2 ? 'done' : ''}`}>
                        <div className="tl-dot" /><div className="tl-label"><strong>Under Review</strong><span>Documents being verified</span></div>
                      </div>
                      <div className={`tl-step ${getStatusStep(loc.status) >= 3 ? (loc.status.toLowerCase() === 'rejected' ? 'rejected' : 'done') : ''}`}>
                        <div className="tl-dot" />
                        <div className="tl-label">
                          <strong>{loc.status.toLowerCase() === 'rejected' ? 'Rejected' : 'Approved'}</strong>
                          <span>{loc.status.toLowerCase() === 'rejected' ? 'Please contact support' : loc.status.toLowerCase() === 'approved' ? 'Welcome to Sermyble!' : 'Decision pending'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        <div className="status-footer">
          <p>Need help? Contact us at <a href="mailto:info@aslinformationservices.com">info@aslinformationservices.com</a> or call <strong>+91 70932 81075</strong></p>
        </div>
      </div>
    </div>
  );
}
